import { useEffect, useState } from "react";

declare global {
    interface Window {
        UC_UI?: {
            showSecondLayer: () => Promise<void>;
            showFirstLayer: () => Promise<void>;
            getServicesBaseInfo: () => Promise<
                Array<{
                    id: string;
                    name: string;
                    categorySlug: string;
                    consent: {
                        status: boolean;
                    };
                }>
            >;
        };
    }
}

export const useMarketingConsent = () => {
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const checkMarketingConsent = async () => {
        if (!window.UC_UI) {
            return false;
        }

        try {
            const services = await window.UC_UI.getServicesBaseInfo();
            const hasMarketingConsent = services
                .filter((s) => s.categorySlug === "marketing")
                .some((s) => s.consent.status === true);
            return hasMarketingConsent;
        } catch (error) {
            console.error("Error checking marketing consent:", error);
            return false;
        }
    };

    const handleConsentChange = async () => {
        const consent = await checkMarketingConsent();
        setMarketingConsent(consent);
    };

    useEffect(() => {
        const checkConsentWithRetry = async () => {
            let attempts = 0;
            const maxAttempts = 20;

            const check = async () => {
                if (window.UC_UI) {
                    const consent = await checkMarketingConsent();
                    setMarketingConsent(consent);
                    setIsLoading(false);
                } else if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(check, 100);
                } else {
                    setIsLoading(false);
                }
            };

            check();
        };

        checkConsentWithRetry();

        // Usercentrics fires this event whenever the user saves consent changes
        const onUcConsent = () => {
            handleConsentChange();
        };
        window.addEventListener("UC_CONSENT", onUcConsent);

        const originalPush = window.dataLayer?.push;
        if (window.dataLayer && originalPush) {
            window.dataLayer.push = function (...args: unknown[]) {
                const result = originalPush.apply(window.dataLayer, args);
                // Check if it's a consent_status event
                const firstArg = args[0] as { event?: string };
                if (firstArg?.event === "consent_status") {
                    handleConsentChange();
                }
                return result;
            };
        }

        return () => {
            window.removeEventListener("UC_CONSENT", onUcConsent);
            if (window.dataLayer && originalPush) {
                window.dataLayer.push = originalPush;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { marketingConsent, isLoading };
};
